<template>
  <div class="page-header">
    <!-- Left Section - Welcome & Info -->
    <div class="header-left">
      <div class="welcome-section">
        <!-- Greeting -->
        <h1 class="greeting-text">
          {{ greetingMessage }}
        </h1>

        <!-- Subheading -->
        <p class="welcome-message">
          {{ welcomeMessage }}
        </p>
      </div>

      <!-- Meta Info -->
      <div class="meta-info">
        <!-- Date Badge -->
        <div class="info-badge date-badge">
          <Clock class="badge-icon" :size="12" />
          <span class="badge-text">{{ formattedDate }}</span>
        </div>

        <!-- Refresh Button -->
        <button v-if="false"
          class="info-badge refresh-btn"
          @click="handleRefresh"
          :disabled="isRefreshing"
          :aria-label="isRefreshing ? $t('Webportal.Refreshing') + '...' : $t('Webportal.Refresh')">
          <RefreshCw
            class="badge-icon"
            :class="{ 'spinning': isRefreshing }"
            :size="15" />
          <span class="badge-text">
            {{ isRefreshing ? $t('Webportal.Refreshing') + '...' : $t('Webportal.Refresh') }}
          </span>
        </button>
      </div>
    </div>

    <!-- Right Section - Hero Image -->
    <div class="header-right">
      <div class="hero-image-container">
        <img
          :src="heroImageUrl"
          :alt="heroImageAlt"
          class="hero-image"
          loading="lazy"
        />
        <div class="image-overlay"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { Clock, RefreshCw } from 'lucide-vue-next';
import PageLayout from '@/componentsv3/PageLayout.vue';
import PageSidebar from '@/componentsv3/PageSidebar.vue';
import SurveysService from '@/services/SurveysService';
// import heroImageDefault from '@/assets/img-bmw/v3/showroom.webp';
import heroImageFallback from '@/assets/img/car-lot.jpg';

export default {
  name: 'PageHeader',

  components: {
    Clock,
    RefreshCw,
    PageLayout,
    PageSidebar
  },

  props: {
    userName: {
      type: String,
      default: 'Given-Name Surname'
    },
    welcomeMessage: {
      type: String,
      default: 'Willkommen zurück im Loop Dashboard'
    },
    heroImageUrl: {
      type: String,
      default: heroImageFallback
    },
    heroImageAlt: {
      type: String,
      default: 'Showroom / Car Lot'
    }
  },

  data() {
    return {
      currentDate: new Date(),
      isRefreshing: false,
      isBusy: false,
      // kpis: [],
      // structureItems: [],
      // clientId: null,
      // clientDetails: null,
      // metrics: null,
      // error: null
    }
  },

  computed: {
    greetingMessage() {
      const hour = new Date().getHours();
      let greeting = 'Guten Tag';

      if (hour < 11) {
        greeting = 'Guten Morgen';
      } else if (hour >= 18) {
        greeting = 'Guten Abend';
      }

      return `${greeting}, ${this.userName}`;
    },

    formattedDate() {
      const dateStr = this.$dayjs(this.currentDate).format('dddd, D. MMMM YYYY');
      return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
    },
  },

  methods: {
    handleRefresh() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;

      // Emit refresh event to parent
      this.$emit('refresh');

      // Simuliere Refresh (entfernen wenn echte API-Calls vorhanden)
      setTimeout(() => {
        this.isRefreshing = false;
        this.currentDate = new Date();
      }, 1500);
    },
  },

  mounted() {
    // Update date every minute
    this.dateInterval = setInterval(() => {
      this.currentDate = new Date();
    }, 60000);
  },

  beforeUnmount() {
    if (this.dateInterval) {
      clearInterval(this.dateInterval);
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  // padding: 24px 32px 32px;

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 400px;
  flex-shrink: 0;

  @media (max-width: 1279px) {
    width: 350px;
  }

  @media (max-width: 991px) {
    width: 100%;
  }
}

.welcome-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.greeting-text {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.31px;
  // color: var(--bmw-primary);
  margin: 0;
  white-space: nowrap;


  @media (max-width: 991px) {
    font-size: 18px;
    white-space: normal;
  }
}

.welcome-message {
  font-size: 14.25px;
  line-height: 21px;
  letter-spacing: -0.17px;
  color: #4a5565;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 14px;
  }
}

.meta-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  @media (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1),
              0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;

  .badge-icon {
    flex-shrink: 0;
    color: currentColor;
  }

  .badge-text {
    font-size: 11.25px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0.05px;
    white-space: nowrap;
    color: inherit;
  }
}

.date-badge {
  color: var(--color-foreground);
  cursor: default;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.header-right {
  flex: 1;
  min-width: 0;
  height: 150px;

  @media (max-width: 991px) {
    width: 100%;
    height: 200px;
  }
}

.hero-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.hero-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: var(--radius-md);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(2, 54, 112, 0.05) 0%,
    rgba(2, 54, 112, 0.02) 100%
  );
  pointer-events: none;
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .welcome-message {
    color: var(--color-muted-foreground);
  }

  .info-badge {
    background-color: var(--color-card);
    border-color: var(--color-border);
    color: var(--color-foreground);
  }

  .date-badge {
    background-color: var(--color-muted);
  }

  .refresh-btn {
    &:hover:not(:disabled) {
      background-color: var(--color-muted);
      border-color: var(--color-border);
    }
  }

  .hero-image-container {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3),
                0 2px 4px -1px rgba(0, 0, 0, 0.2);
  }

  .image-overlay {
    background: linear-gradient(
      135deg,
      rgba(2, 54, 112, 0.15) 0%,
      rgba(2, 54, 112, 0.05) 100%
    );
  }
}

// Animations & Transitions
* {
  transition-property: background-color, border-color, color, transform, box-shadow;
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;
}

// Accessibility
.refresh-btn:focus-visible {
  outline: 2px solid var(--bmw-primary);
  outline-offset: 2px;
}
</style>
